export const PETS_DOCUMENT_FOLDER_KEY = 'pets';

export type SharedPetDocumentInput = {
  folder?: string | null;
  fileName?: string | null;
  uploadedAt?: string | null;
};

export type SharedPetExpenseInput = {
  amount: number;
  category?: string | null;
  date?: string | null;
  description?: string | null;
  isRecurring?: boolean | null;
  recurringPeriod?: string | null;
};

export type SharedPetRecurringOutgoingInput = {
  merchant: string;
  category?: string | null;
  count: number;
  averageAmount: number;
};

function normaliseFolderKey(value: string | null | undefined) {
  return String(value || '').trim().toLowerCase();
}

function normaliseCategory(value: string | null | undefined) {
  return String(value || '').trim().toLowerCase();
}

function safeDateValue(value: string | null | undefined) {
  const iso = String(value || '').trim();
  if (!iso) return '';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
}

function currentMonthKey(now?: string | Date) {
  const parsed = now instanceof Date ? now : now ? new Date(now) : new Date();
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString().slice(0, 7);
  }
  return parsed.toISOString().slice(0, 7);
}

export function isPetDocumentFolder(folder: string | null | undefined) {
  return normaliseFolderKey(folder) === PETS_DOCUMENT_FOLDER_KEY;
}

export function isPetSpendCategory(category: string | null | undefined) {
  return normaliseCategory(category) === 'pets';
}

export function filterPetDocuments<T extends { folder?: string | null }>(documents: readonly T[]) {
  return documents.filter((document) => isPetDocumentFolder(document.folder));
}

export function filterPetExpenses<T extends { category?: string | null }>(expenses: readonly T[]) {
  return expenses.filter((expense) => isPetSpendCategory(expense.category));
}

export function filterPetRecurringOutgoings<T extends { category?: string | null }>(items: readonly T[]) {
  return items.filter((item) => isPetSpendCategory(item.category));
}

export function buildPetDocumentsSummary(input: {
  documents: readonly SharedPetDocumentInput[];
}) {
  const documents = [...filterPetDocuments(input.documents)].sort((left, right) =>
    safeDateValue(right.uploadedAt).localeCompare(safeDateValue(left.uploadedAt)),
  );
  const latestDocument = documents[0] || null;
  const count = documents.length;

  return {
    count,
    latestFileName: latestDocument?.fileName?.trim() || null,
    latestUploadedAt: latestDocument?.uploadedAt || null,
    hasDocuments: count > 0,
  };
}

export function buildPetSpendSummary(input: {
  expenses: readonly SharedPetExpenseInput[];
  recurringOutgoings?: readonly SharedPetRecurringOutgoingInput[];
  now?: string | Date;
}) {
  const expenses = [...filterPetExpenses(input.expenses)];
  const recurringOutgoings = filterPetRecurringOutgoings(input.recurringOutgoings || []);
  const monthKey = currentMonthKey(input.now);

  const currentMonthExpenses = expenses.filter((expense) => String(expense.date || '').startsWith(monthKey));
  const currentMonthTotal = currentMonthExpenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
  const manualRecurringCount = expenses.filter(
    (expense) => Boolean(expense.isRecurring) && expense.recurringPeriod && expense.recurringPeriod !== 'none',
  ).length;

  const latestExpense = [...expenses].sort((left, right) =>
    safeDateValue(right.date).localeCompare(safeDateValue(left.date)),
  )[0] || null;

  return {
    expenseCount: expenses.length,
    currentMonthExpenseCount: currentMonthExpenses.length,
    currentMonthTotal,
    manualRecurringCount,
    connectedRecurringCount: recurringOutgoings.length,
    recurringCount: manualRecurringCount + recurringOutgoings.length,
    latestExpenseDescription: latestExpense?.description?.trim() || null,
    latestExpenseDate: latestExpense?.date || null,
    latestExpenseAmount: latestExpense ? Number(latestExpense.amount || 0) : null,
    hasAnySpend: expenses.length > 0 || recurringOutgoings.length > 0,
  };
}
